import React from 'react';
import { ReportData } from '../../types/report.types';

interface OwaspTop10EditorV2Props {
  reportData: ReportData;
  onDataChange: (field: string, value: any) => void;
}

const owaspCategories = [
  { code: 'A01:2025', name: 'Broken Access Control', key: 'broken access control', defaultKey: 'A01' },
  { code: 'A02:2025', name: 'Security Misconfiguration', key: 'security misconfiguration', defaultKey: 'A02' },
  { code: 'A03:2025', name: 'Software Supply Chain Failures', key: 'software supply chain failures', defaultKey: 'A03' },
  { code: 'A04:2025', name: 'Cryptographic Failures', key: 'cryptographic failures', defaultKey: 'A04' },
  { code: 'A05:2025', name: 'Injection', key: 'injection', defaultKey: 'A05' },
  { code: 'A06:2025', name: 'Insecure Design', key: 'insecure design', defaultKey: 'A06' },
  { code: 'A07:2025', name: 'Authentication Failures', key: 'authentication', defaultKey: 'A07' },
  { code: 'A08:2025', name: 'Software or Data Integrity Failures', key: 'integrity failures', defaultKey: 'A08' },
  { code: 'A09:2025', name: 'Security Logging and Alerting Failures', key: 'logging and monitoring', defaultKey: 'A09' },
  { code: 'A10:2025', name: 'Mishandling of Exceptional Conditions', key: 'mishandling of exceptional conditions', defaultKey: 'A10' },
];

const OwaspTop10EditorV2: React.FC<OwaspTop10EditorV2Props> = ({ reportData, onDataChange }) => {
  const config = reportData.finding_config || { globalFields: { description: true, instructions: true, impact: true, fix: true, evidence: true }, individualFindings: {} };
  const owaspCoverage = config.owaspCoverage || {};
  const findings = reportData.detailed_findings || [];

  const defaultCategories = [
    { code: 'A01:2025', name: 'Broken Access Control', key: 'broken access control' },
    { code: 'A02:2025', name: 'Security Misconfiguration', key: 'security misconfiguration' },
    { code: 'A03:2025', name: 'Software Supply Chain Failures', key: 'software supply chain failures' },
    { code: 'A04:2025', name: 'Cryptographic Failures', key: 'cryptographic failures' },
    { code: 'A05:2025', name: 'Injection', key: 'injection' },
    { code: 'A06:2025', name: 'Insecure Design', key: 'insecure design' },
    { code: 'A07:2025', name: 'Authentication Failures', key: 'authentication' },
    { code: 'A08:2025', name: 'Software or Data Integrity Failures', key: 'integrity failures' },
    { code: 'A09:2025', name: 'Security Logging and Alerting Failures', key: 'logging and monitoring' },
    { code: 'A10:2025', name: 'Mishandling of Exceptional Conditions', key: 'mishandling of exceptional conditions' }
  ];

  const categories = config.owaspCustomCategories || defaultCategories;

  const getCategoryStatus = (categoryKey: string) => {
    // Find all findings matching this category
    const matched = findings.filter(f => {
      const owaspStr = (f.owasp_category || f.owasp || '').toLowerCase();
      return owaspStr.includes(categoryKey.toLowerCase());
    });

    if (matched.length > 0) {
      const ids = matched.map(m => m.report_id || m.id || '').filter(Boolean).join(', ');
      return { status: 'Findings', text: `Findings (${ids})`, isClean: false };
    }
    return { status: 'Clean', text: 'Clean', isClean: true };
  };

  const handleToggleTested = (code: string) => {
    const current = owaspCoverage[code] || { tested: true };
    const updated = {
      ...owaspCoverage,
      [code]: {
        ...current,
        tested: !current.tested
      }
    };
    onDataChange('finding_config', { ...config, owaspCoverage: updated });
  };

  const handleResultOverrideChange = (code: string, value: string) => {
    const current = owaspCoverage[code] || { tested: true };
    const updated = {
      ...owaspCoverage,
      [code]: {
        ...current,
        resultOverride: value || undefined
      }
    };
    onDataChange('finding_config', { ...config, owaspCoverage: updated });
  };

  const handleHeadingChange = (value: string) => {
    onDataChange('finding_config', { ...config, owaspHeading: value });
  };

  const handleDescriptionChange = (value: string) => {
    onDataChange('finding_config', { ...config, owaspDescription: value });
  };

  const handleCategoryFieldChange = (index: number, field: 'code' | 'name' | 'key', value: string) => {
    const updated = categories.map((cat: any, i: number) => {
      if (i === index) {
        return { ...cat, [field]: value };
      }
      return cat;
    });
    onDataChange('finding_config', { ...config, owaspCustomCategories: updated });
  };

  return (
    <div className="h-full overflow-y-auto space-y-6">
      <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full" />
          <div>
            <h3 className="text-base font-bold text-blue-900 tracking-tight">OWASP Top 10 Coverage Editor</h3>
            <p className="text-xs text-blue-700/80 font-medium mt-0.5">Toggle category evaluation status, modify headings/descriptions, or override the finding/clean results manually</p>
          </div>
        </div>

        {/* Heading & Description Editing */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-4 border-b border-slate-100 mb-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Section Heading Title</label>
            <input
              type="text"
              value={config.owaspHeading || '4. OWASP Top 10 (2025) Coverage'}
              onChange={(e) => handleHeadingChange(e.target.value)}
              className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
              placeholder="e.g., 4. OWASP Top 10 (2025) Coverage"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Sub-heading Description Text</label>
            <textarea
              value={config.owaspDescription || 'Coverage of each OWASP Top 10 category during this engagement and whether issues were identified.'}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
              rows={2}
              placeholder="Provide a description paragraph..."
            />
          </div>
        </div>

        <div className="space-y-4 pt-2">
          {categories.map((cat: any, index: number) => {
            const configCode = cat.code.includes(':') ? cat.code : `${cat.code}:2025`;
            const settings = owaspCoverage[cat.code] || owaspCoverage[configCode] || { tested: true, resultOverride: '' };
            const calculated = getCategoryStatus(cat.key);
            return (
              <div key={index} className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex flex-col gap-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Editable Fields: Code, Name, Mapping Key */}
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5 font-bold">Code</label>
                      <input
                        type="text"
                        value={cat.code}
                        onChange={(e) => handleCategoryFieldChange(index, 'code', e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 font-bold"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Name</label>
                      <input
                        type="text"
                        value={cat.name}
                        onChange={(e) => handleCategoryFieldChange(index, 'name', e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5 font-bold">Mapping Keyword</label>
                      <input
                        type="text"
                        value={cat.key}
                        onChange={(e) => handleCategoryFieldChange(index, 'key', e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 font-mono text-blue-700"
                        placeholder="Keyword to match findings category"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-4 flex-shrink-0 align-bottom pt-4">
                    <div className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        id={`tested-${index}`}
                        checked={settings.tested !== false}
                        onChange={() => handleToggleTested(cat.code)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor={`tested-${index}`} className="text-xs font-semibold text-slate-700 select-none">
                        Tested
                      </label>
                    </div>

                    <div>
                      <input
                        type="text"
                        value={settings.resultOverride || ''}
                        onChange={(e) => handleResultOverrideChange(cat.code, e.target.value)}
                        placeholder={calculated.text}
                        className="w-56 px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OwaspTop10EditorV2;
